import React, { useCallback, useContext, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@adscore/with-responsive-size';

import { AutoPlayLayout } from './auto-play-layout';
import { AdChannelContainer } from './ad-channel-container';
import {
  AdPlacementList,
  getAdChannelForPlacement as defaultGetAdChannelForPlacement,
  getAdDataForPlacement as defaultGetAdDataForPlacement,
  getExtraPlacementInfo as defaultGetExtraPlacementInfo,
} from './ad-preview-config';
import { getAutoPlayAdPreviewsStyles } from './auto-play-ad-previews-styles';

export const AutoPlayAdPreviews = forwardRef(({
  adPlacementList,
  getAdChannelForPlacement,
  getAdDataForPlacement,
  getExtraPlacementInfo,
  onCurrentPlacementChange,
  customPlacementIndex,
}, ref) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getAutoPlayAdPreviewsStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);

  const onRenderPlacement = useCallback((placementData, AdPlacementComponent) => {
    const { placementMeta } = placementData || {};
    const { placementType } = placementMeta || {};
    const {
      badgeText,
      onRenderBadgeIcon,
      onRenderChannelIcon,
    } = getAdChannelForPlacement(placementType) || {};

    return (
      <AdChannelContainer
        badgeText={badgeText}
        onRenderBadgeIcon={onRenderBadgeIcon}
        onRenderChannelIcon={onRenderChannelIcon}
      >
        <AdPlacementComponent
          placementData={placementData}
          i18n={i18n}
          {...getAdDataForPlacement(placementType)}
          {...getExtraPlacementInfo(placementType)}
        />
      </AdChannelContainer>
    );
  }, [getAdChannelForPlacement, getAdDataForPlacement, getExtraPlacementInfo, i18n]);

  return (
    <div ref={ref} className={classNames('auto-play-ad-previews', classes.container, classes[`container-${displayMode}`])}>
      <AutoPlayLayout
        placementList={adPlacementList}
        onRenderPlacement={onRenderPlacement}
        onCurrentPlacementChange={onCurrentPlacementChange}
        customPlacementIndex={customPlacementIndex}
      />
    </div>
  );
});

AutoPlayAdPreviews.propTypes = {
  adPlacementList: PropTypes.arrayOf(PropTypes.shape({
    placementMeta: PropTypes.shape({}),
    imageMeta: PropTypes.shape({}),
  })),
  getAdChannelForPlacement: PropTypes.func,
  getAdDataForPlacement: PropTypes.func,
  getExtraPlacementInfo: PropTypes.func,
  onCurrentPlacementChange: PropTypes.func,
  customPlacementIndex: PropTypes.number,
};

AutoPlayAdPreviews.defaultProps = {
  adPlacementList: AdPlacementList,
  getAdChannelForPlacement: defaultGetAdChannelForPlacement,
  getAdDataForPlacement: defaultGetAdDataForPlacement,
  getExtraPlacementInfo: defaultGetExtraPlacementInfo,
  onCurrentPlacementChange: undefined,
  customPlacementIndex: undefined,
};
